import { groupBy, orderBy } from 'es-toolkit';
import { produce } from 'immer';

import { upsertLocally } from '@paragon/client-tools';
import {
  BearingEnvelope,
  FeaturePatternTrussContainer,
  FloorPolyline3D,
  FloorPolylineSegmentGeometry,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { Point2D } from '@paragon/geometry-library';
import { getEnvelopeSegmentFromEnvelope } from '@paragon/layout';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { forceArray } from '@paragon/utilities';

import { createSnapDerivedGeometry } from '../../../library/layout-geometry/snap-derived-geometry';
import { EntitySnapTarget } from '../../../library/layout-snapping/EntitySnapTarget';
import { findNextName } from '../../../library/utilities/findNextName';
import { RootState } from '../../index';
import { getSingleSelectedItemEntryOrNull } from '../../selectors/getSelectedItemSelector';
import {
  getActiveLayoutLevelGuidOrNull,
  getAllFeaturePatternContainersDictionary,
  getFeaturePatternContainerElevationsFromActiveLevel,
} from '../../selectors/layoutDataSelectors';
import {
  applicationOnInsertingFeaturePatternContainer,
  applicationOnManuallyPickingInsertionFeaturePatternContainerBoundary,
  InsertionFeaturePatternContainer,
  updateInsertionFeaturePatternContainer,
} from '../../slices/applicationSlice';
import { layoutSlice } from '../../slices/layoutSlice';
import { ensureLevelIsVisible } from '../levelVisibilityActions';
import { loadFeaturePatternSettings } from '../loadActions';
import { selectItem } from '../selectActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from '../thunkActions';

type FeaturePatternContainerElevationDefaults = Partial<Pick<FloorPolyline3D, 'topElevation' | 'bottomElevation'>>;

function getMostCommonOrHighestEnvelopeElevation(envelopes: BearingEnvelope[]): number | null {
  const envelopesByTopElevation = groupBy(envelopes, (envelope) => envelope.top);

  const mostCommonTopEntry = orderBy(
    Object.entries(envelopesByTopElevation).map(([topKey, topEnvelopes]) => [Number(topKey), topEnvelopes] as const),
    [([, topEnvelopes]) => topEnvelopes.length, ([top]) => top],
    ['desc', 'desc'],
  )[0];

  return mostCommonTopEntry == null ? null : mostCommonTopEntry[0];
}

function getFeaturePatternContainerElevationsFromHeuristics(
  state: RootState,
): FeaturePatternContainerElevationDefaults {
  const openContext = state.application.openContext;
  if (openContext?.type !== 'project') return {};

  const relevantEnvelopes = Object.values(state.layout.bearingEnvelopes).filter((envelope) => !envelope.nonStructural);
  const mostCommonTop = getMostCommonOrHighestEnvelopeElevation(relevantEnvelopes);

  if (mostCommonTop == null) return {};

  const depth = state.settingsDatabase.featurePatternSettings[openContext.projectGuid]?.depth;
  return depth != null
    ? {
        bottomElevation: mostCommonTop,
        topElevation: mostCommonTop + depth,
      }
    : { bottomElevation: mostCommonTop };
}

export const startInsertingFeaturePatternContainer = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();
  const levelGuid = getActiveLayoutLevelGuidOrNull(state);

  let elevationsFromHeuristics: FeaturePatternContainerElevationDefaults = {};
  if (levelGuid == null && state.application.openContext?.type === 'project') {
    await dispatch(loadFeaturePatternSettings(state.application.openContext.projectGuid));
    elevationsFromHeuristics = getFeaturePatternContainerElevationsFromHeuristics(getState());
  }

  const existingContainers = Object.values(state.layout.featurePatternContainers);
  const takenNames = existingContainers.map((container) => container.name);

  const name = findNextName(takenNames);

  const elevationsFromPreviousInsertion = state.layout.featurePatternContainerInsertionSettings.defaultElevations ?? {};
  const elevationsFromLevel = getFeaturePatternContainerElevationsFromActiveLevel(state);
  const defaultElevations = {
    topElevation: 0,
    bottomElevation: 0,

    ...elevationsFromHeuristics,
    ...elevationsFromPreviousInsertion,
    ...elevationsFromLevel,
  };

  const insertionContainer = {
    name,
    levelGuid,
    shape: { geometry: [], ...defaultElevations },
    slopeSettings: state.layout.featurePatternContainerInsertionSettings.defaultSlopeSettings,
    featureAreas: [],
    exclusions: [],
  } satisfies InsertionFeaturePatternContainer;

  if (levelGuid) dispatch(ensureLevelIsVisible(levelGuid, { exclusive: true }));

  dispatch(applicationOnInsertingFeaturePatternContainer(insertionContainer));
};

export const finishInsertingFeaturePatternContainer = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const { insertingObjectInfo } = getState().application;
  if (insertingObjectInfo?.type !== 'featurePatternContainer') return;

  const createdContainer = {
    guid: crypto.randomUUID(),
    ...insertingObjectInfo.data,
  };

  dispatch(layoutSlice.actions.featurePatternContainerCreated(createdContainer));
  dispatch(selectItem(createdContainer, 'featurePatternContainer'));

  await dispatch(saveFeaturePatternContainers());
};

export type FeaturePatternContainerPatchOrUpdateFn =
  | Partial<Omit<FeaturePatternTrussContainer, 'guid'>>
  | ((base: Omit<FeaturePatternTrussContainer, 'guid'>) => Partial<Omit<FeaturePatternTrussContainer, 'guid'>>);

export function getFeaturePatternContainerPatch(
  patchOrUpdateFn: FeaturePatternContainerPatchOrUpdateFn,
  container: Omit<FeaturePatternTrussContainer, 'guid'>,
): Partial<Omit<FeaturePatternTrussContainer, 'guid'>> {
  return typeof patchOrUpdateFn === 'function' ? patchOrUpdateFn(container) : patchOrUpdateFn;
}

export const editActiveFeaturePatternContainer =
  (patchOrUpdateFn: FeaturePatternContainerPatchOrUpdateFn): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    const { insertingObjectInfo } = state.application;
    const selectedEntry = getSingleSelectedItemEntryOrNull(state);

    if (insertingObjectInfo?.type === 'featurePatternContainer') {
      dispatch(
        updateInsertionFeaturePatternContainer(
          getFeaturePatternContainerPatch(patchOrUpdateFn, insertingObjectInfo.data),
        ),
      );
    } else if (selectedEntry?.type === 'featurePatternContainer') {
      await dispatch(
        editFeaturePatternContainers({
          guid: selectedEntry.item.guid,
          changes: patchOrUpdateFn,
        }),
      );
    } else {
      dispatch(enqueueError('Unable to edit feature pattern container. Please contact Paragon Customer Support.'));
      throw new Error('Failed to edit feature pattern container -- invalid state.');
    }
  };

export type FeaturePatternContainerEditInstructions = {
  guid: string;
  changes: FeaturePatternContainerPatchOrUpdateFn;
};

export const editFeaturePatternContainers =
  (
    instructions: FeaturePatternContainerEditInstructions | FeaturePatternContainerEditInstructions[],
    options?: {
      createdContainers?: FeaturePatternTrussContainer[];
    },
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    let existingContainers = getAllFeaturePatternContainersDictionary(state);
    if (options?.createdContainers) {
      existingContainers = upsertLocally(existingContainers, options.createdContainers);
    }

    const editedContainers = forceArray(instructions).map(({ guid, changes }) => {
      const originalContainer = existingContainers[guid];
      if (originalContainer == null) {
        dispatch(
          enqueueError(
            'Unable to edit feature pattern container. Please try again or contact Paragon Customer Support.',
          ),
        );
        throw new Error(`Unable to find feature pattern container '${guid}' in order to apply edits.`);
      }

      const patch = getFeaturePatternContainerPatch(changes, originalContainer);
      return { ...originalContainer, ...patch };
    });

    dispatch(layoutSlice.actions.featurePatternContainersUpserted(editedContainers));
    await dispatch(saveFeaturePatternContainers());
  };

export const beginRedefiningActiveFeaturePatternContainerGeometry = (): ThunkActionVoid => (dispatch, getState) => {
  dispatch(editActiveFeaturePatternContainer((container) => ({ shape: { ...container.shape, geometry: [] } })));

  // in insertion mode that's all it takes, but if editing an existing area we need to manually enter picking mode now
  if (getState().application.insertingObjectInfo?.type !== 'featureArea')
    dispatch(applicationOnManuallyPickingInsertionFeaturePatternContainerBoundary());
};

export const saveFeaturePatternContainers = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();
  if (state.application.openContext?.type !== 'project') return;

  const spinnerId = dispatch(addSpinnerMessage('Saving feature pattern containers...'));

  try {
    await designService.projects.replaceFeaturePatternContainers(
      state.application.openContext.projectGuid,
      state.layout.featurePatternContainers,
    );
  } catch (error) {
    console.error(error);
    dispatch(
      enqueueError('Unable to save feature pattern containers. Please try again or contact Paragon Customer Support.'),
    );
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

export const handleFeaturePatternContainerBoundarySegmentPicked =
  (entitySnap: EntitySnapTarget): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    const createdSegment = createSnapDerivedGeometry(entitySnap, {
      primaryBearingSide: 'front',
      bearingEnvelopes: state.layout.bearingEnvelopes,
    }).asFloorPolylineSegment();

    await dispatch(
      editActiveFeaturePatternContainer(
        produce((container) => {
          if (container.shape) {
            container.shape.geometry.push(createdSegment);
          } else {
            container.shape = {
              topElevation: 0,
              bottomElevation: 0,
              geometry: [createdSegment],
            };
          }
        }),
      ),
    );
  };

export const disassociateFeaturePatternContainersFromBearings =
  (bearings: readonly BearingEnvelope[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const updatedContainers: FeaturePatternTrussContainer[] = [];
    for (const container of Object.values(getState().layout.featurePatternContainers)) {
      const { geometry } = container.shape;
      let updated = false;
      const newGeometry = geometry.map((cut): FloorPolylineSegmentGeometry => {
        if (cut.type === 'AbsoluteVertical') return cut;
        const deletedBearingDefiningCut = bearings.find(({ guid }) => guid === cut.bearingEnvelopeGuid);
        if (!deletedBearingDefiningCut) return cut;

        updated = true;
        const definingSegment = getEnvelopeSegmentFromEnvelope(
          deletedBearingDefiningCut,
          cut.flipped ? 'Back' : 'Front',
        )?.toObject() ?? { basePoint: Point2D.origin.toObject(), endPoint: Point2D.origin.toObject() };
        return { type: 'AbsoluteVertical', segment: definingSegment, isInfinite: false };
      });

      if (updated) updatedContainers.push({ ...container, shape: { ...container.shape, geometry: newGeometry } });
    }

    if (updatedContainers.length !== 0) {
      dispatch(layoutSlice.actions.featurePatternContainersUpserted(updatedContainers));
      await dispatch(saveFeaturePatternContainers());
    }
  };
